import { ApiAuthStrategy } from "@/domains/auth/types";
import Cookies from "js-cookie";
import { DateTime } from "luxon";
import { PublicAppSubStoreArgs, PublicAppSubStore } from "@/store/types";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { Uuid } from "@/domains/global/identifiers";
import localDb from "@/domains/local-db";
import { api, authApi } from "@/modules/api";
import { enumModule } from "@/modules/enum";
import { BaseError } from "@/domains/errors";
import pTimeout from "p-timeout";
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
  buildAsyncData,
} from "@/domains/async/AsyncData";
import { GuestInfo } from "@/store/auth/types";
import { isString } from "lodash-es";
import { toastModule } from "@/modules/toast";
import { appRoutes } from "@/app/router/appRoutes";
import { windowModule } from "@/modules/window";
import { clientEnvModule } from "@/modules/client-env";
import { localStorageModule } from "@/modules/local-storage";
import { UPGRADED_USER_EMAIL_SEARCH_PARAM } from "@/modules/google-identity/constants";
import { EXTENSION_CLOSE_WINDOW_COOKIE_NAME } from "@/domains/extension";
import { AssetAccessTokenState } from "@/store/auth/AssetAccessTokenState";
import { metricsModule } from "@/modules/metrics";
import { electronModule } from "@/modules/electron";
import { LastUsedAuthMethod, LastUsedAuthMethodRecord } from "@/domains/auth/last-used-auth-method";

export type EmailPasswordAuthResult = {
  success: boolean;
  errorMessage?: string;
};

const normalizeEmailPasswordAuthErrorMessage = (message: string): string => {
  const trimmedMessage = message.trim();

  if (!trimmedMessage.includes("\n") && trimmedMessage.endsWith(".")) {
    return trimmedMessage.slice(0, -1);
  }

  return trimmedMessage;
};

const extractEmailPasswordAuthErrorMessage = ({
  rawError,
  fallback,
}: {
  rawError: unknown;
  fallback: string;
}): string => {
  if (isString(rawError) && rawError) {
    return normalizeEmailPasswordAuthErrorMessage(rawError);
  }

  if (!rawError || typeof rawError !== "object") {
    return fallback;
  }

  const error = rawError as {
    detail?: unknown;
    errors?: Array<{ detail?: unknown }>;
    non_field_errors?: unknown;
    error_metadata?: { message?: unknown };
  };
  const candidates = [
    error.error_metadata?.message,
    error.detail,
    Array.isArray(error.detail) ? error.detail[0] : undefined,
    Array.isArray(error.non_field_errors) ? error.non_field_errors[0] : undefined,
    error.errors?.find(item => isString(item.detail))?.detail,
  ];
  const message = candidates.find(isString);

  return normalizeEmailPasswordAuthErrorMessage(message ?? fallback);
};

export class AppStoreAuthStore extends PublicAppSubStore {
  activeAuthToken: string | null = null;
  activeAuthStrategy: ApiAuthStrategy | null = null;
  lastUsedAuthMethodRecord: LastUsedAuthMethodRecord | null = null;
  assetAccessTokenState: AssetAccessTokenState;
  initializationState = buildAsyncData<boolean>({});
  authenticateAccountUsingAppleOAuthStateState = buildAsyncData<boolean>({});
  authenticateAccountUsingGoogleOAuthCodeState = buildAsyncData<boolean>({});
  authenticateAccountUsingEmailPasswordState = buildAsyncData<boolean>({});
  authenticateAccountUsingOAuthTokenState = buildAsyncData<boolean>({});
  guestInfo?: GuestInfo;
  loggedInDuringThisSession = false;
  private static readonly PERIODIC_CHECK_INTERVAL = 2500;
  private periodicCheckIntervalId: ReturnType<typeof globalThis.setInterval> | null = null;

  constructor(injectedDeps: PublicAppSubStoreArgs) {
    super(injectedDeps);

    this.assetAccessTokenState = new AssetAccessTokenState({ globalCache: this.store.globalCache });

    makeObservable<
      AppStoreAuthStore,
      | "periodicCheckIntervalId"
      | "startPeriodicCheck"
      | "stopPeriodicCheck"
      | "periodicCheckIfAuthed"
      | "clearBackendExtensionAuthCookie"
      | "handleExtensionAuthWindowClose"
    >(this, {
      assetAccessTokenState: true,
      startPeriodicCheck: true,
      stopPeriodicCheck: true,
      periodicCheckIntervalId: true,
      checkAndSetAuthInfo: action,
      periodicCheckIfAuthed: true,
      resetSystemStateForLogIn: true,
      resetAuthenticateAccountUsingAppleOAuthStateState: action,
      updateAuthenticateAccountUsingAppleOAuthStateState: true,
      authenticateAccountUsingAppleOAuthState: true,
      updateAuthenticateAccountUsingOAuthTokenState: true,
      authenticateAccountUsingOAuthTokenState: true,
      authenticateAccountUsingOAuthToken: true,
      initialize: action,
      processSearchParams: action,
      storeAuthToken: action,
      clearAuthToken: action,
      logOut: action,
      authenticateAccountUsingGoogleOAuthCode: action,
      authenticateAccountUsingEmailPassword: action,
      requestEmailPasswordReset: true,
      requestEmailPasswordVerification: true,
      resetEmailPassword: true,
      signUpUsingEmailPassword: true,
      verifyEmailPasswordEmailAddress: true,
      activeAuthToken: observable,
      activeAuthStrategy: observable,
      initializationState: observable,
      authenticateAccountUsingAppleOAuthStateState: observable,
      authenticateAccountUsingGoogleOAuthCodeState: observable,
      authenticateAccountUsingEmailPasswordState: observable,
      guestInfo: observable,
      lastUsedAuthMethodRecord: observable,
      isAuthenticated: computed,
      isStandardMode: computed,
      isGuestMode: computed,
      isGuestModeOrStandardMode: computed,
      lastUsedAuthMethod: computed,
      updateInitializationState: action,
      updateAuthenticateAccountUsingGoogleOAuthCodeState: action,
      updateAuthenticateAccountUsingEmailPasswordState: action,
      updateLastUsedAuthMethodRecord: action,
      setLastUsedAuthMethod: action,
      clearLastUsedAuthMethod: action,
      loggedInDuringThisSession: observable,
      clearBackendExtensionAuthCookie: true,
      handleExtensionAuthWindowClose: true,
    });

    reaction(
      () => this.activeAuthToken,
      authToken => {
        api.setAuthToken(authToken);
        this.handleExtensionAuthWindowClose(authToken);
      }
    );

    reaction(
      () => ({
        token: this.activeAuthToken,
        strategy: this.activeAuthStrategy,
      }),
      ({ token, strategy }) => {
        if (strategy === ApiAuthStrategy.Account && token) {
          void this.assetAccessTokenState.ensureToken();
        } else {
          this.assetAccessTokenState.reset();
        }
      }
    );
  }

  get isAuthenticated() {
    return this.isStandardMode;
  }

  get isStandardMode() {
    return this.activeAuthStrategy === ApiAuthStrategy.Account;
  }

  get isGuestMode() {
    return this.activeAuthStrategy === ApiAuthStrategy.GuestAccount;
  }

  get isGuestModeOrStandardMode() {
    return this.isStandardMode || this.isGuestMode;
  }

  get lastUsedAuthMethod() {
    return this.lastUsedAuthMethodRecord?.method ?? null;
  }

  updateInitializationState = (f: (state: AsyncData<boolean>) => AsyncData<boolean>) => {
    this.initializationState = f(this.initializationState);
  };

  updateAuthenticateAccountUsingGoogleOAuthCodeState = (
    f: (state: AsyncData<boolean>) => AsyncData<boolean>
  ) => {
    this.authenticateAccountUsingGoogleOAuthCodeState = f(
      this.authenticateAccountUsingGoogleOAuthCodeState
    );
  };

  updateAuthenticateAccountUsingEmailPasswordState = (
    f: (state: AsyncData<boolean>) => AsyncData<boolean>
  ) => {
    this.authenticateAccountUsingEmailPasswordState = f(
      this.authenticateAccountUsingEmailPasswordState
    );
  };

  updateAuthenticateAccountUsingOAuthTokenState = (
    f: (state: AsyncData<boolean>) => AsyncData<boolean>
  ) => {
    this.authenticateAccountUsingOAuthTokenState = f(this.authenticateAccountUsingOAuthTokenState);
  };

  updateAuthenticateAccountUsingAppleOAuthStateState = (
    f: (state: AsyncData<boolean>) => AsyncData<boolean>
  ) => {
    this.authenticateAccountUsingAppleOAuthStateState = f(
      this.authenticateAccountUsingAppleOAuthStateState
    );
  };

  resetAuthenticateAccountUsingAppleOAuthStateState = () => {
    this.authenticateAccountUsingAppleOAuthStateState = buildAsyncData({});
  };

  updateLastUsedAuthMethodRecord = (record: LastUsedAuthMethodRecord | null) => {
    this.lastUsedAuthMethodRecord = record;
  };

  setLastUsedAuthMethod = async (method: LastUsedAuthMethod) => {
    const record = localStorageModule.writeLastUsedAuthMethod(method);

    this.updateLastUsedAuthMethodRecord(
      record ?? {
        method,
        updatedAtMs: DateTime.now().toMillis(),
      }
    );
  };

  clearLastUsedAuthMethod = async () => {
    localStorageModule.clearLastUsedAuthMethod();
    this.updateLastUsedAuthMethodRecord(null);
  };

  initialize = async () => {
    this.updateInitializationState(asyncDataLoading);

    try {
      this.updateLastUsedAuthMethodRecord(localStorageModule.readLastUsedAuthMethod());
      await this.checkAndSetAuthInfo();
    } catch (err) {
      logger.error({
        message: "[AppStoreAuthStore] checkAndSetAuthInfo failed",
        info: { err: objectModule.safeErrorAsJson(err as BaseError) },
      });
    } finally {
      this.updateInitializationState(asyncData => asyncDataLoaded(asyncData, true));
      this.startPeriodicCheck();
    }
  };

  // When tab is not authenticated, periodically check if any other tab has been authenticated.
  private periodicCheckIfAuthed = async () => {
    if (this.isAuthenticated) {
      this.stopPeriodicCheck();
      return;
    }

    logger.debug({
      message: "[AppStoreAuthStore] isAuthenticated false, checking if authed",
    });

    try {
      await this.checkAndSetAuthInfo();
    } catch (err) {
      this.stopPeriodicCheck();
      logger.error({
        message: "[AppStoreAuthStore] checkAndSetAuthInfo failed",
        info: { err: objectModule.safeErrorAsJson(err as BaseError) },
      });
    }
  };

  checkAndSetAuthInfo = async () => {
    const accountInfo = await localDb.global.getCurrentAuthInfo();

    if (accountInfo?.authToken && accountInfo?.authStrategy) {
      const validValues: string[] = enumModule.values(ApiAuthStrategy);

      if (validValues.includes(accountInfo.authStrategy)) {
        logger.debug({
          message: "[AppStoreAuthStore] checkAndSetAuthInfo success",
          info: { accountInfo },
        });

        runInAction(() => {
          this.activeAuthToken = accountInfo.authToken;
          this.activeAuthStrategy = accountInfo.authStrategy as ApiAuthStrategy;
        });
      }
    }
  };

  processSearchParams = ({
    searchParams,
    setSearchParams,
  }: {
    searchParams: URLSearchParams;
    setSearchParams: (searchParams: URLSearchParams) => void;
  }) => {
    const guestInfoParam = searchParams.get("guest_info");
    if (guestInfoParam && !this.isAuthenticated) {
      try {
        const json = objectModule.fromJson(atob(decodeURIComponent(guestInfoParam)));

        runInAction(() => {
          this.guestInfo = {
            spaceAccountId: getStringFromJson(json, "invitee_guest_account_id"),
            inviter: {
              spaceAccountId:
                getStringFromJson(json, "inviter_space_account_id") ??
                "fake-inviter-space-account-id",
              profileEmailAddress: "",
              profileDisplayName: getStringFromJson(json, "inviter_display_name") ?? "Someone",
              profilePhotoUrl: getStringFromJson(json, "inviter_photo_url"),
            },
            noteTitle: getStringFromJson(json, "note_title"),
          };
          this.activeAuthToken = getStringFromJson(json, "guest_access_token") ?? null;
          this.activeAuthStrategy = ApiAuthStrategy.GuestAccount;
        });
      } catch (err) {
        logger.error({
          message: "[processSearchParams] Failed to parse guest info.",
          info: { err: objectModule.safeErrorAsJson(err as BaseError) },
        });
      }
    }

    const upgradedUserEmailParam = searchParams.get(UPGRADED_USER_EMAIL_SEARCH_PARAM);
    if (upgradedUserEmailParam) {
      localStorageModule.writeUpgradedUserEmail(upgradedUserEmailParam);

      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete(UPGRADED_USER_EMAIL_SEARCH_PARAM);
      // Don't set state during react render.
      setTimeout(() => setSearchParams(newSearchParams), 0);
    }
  };

  storeAuthToken = async ({
    accountId,
    authToken,
    authStrategy,
  }: {
    accountId: Uuid;
    authToken: string;
    authStrategy: ApiAuthStrategy;
  }) => {
    try {
      await localDb.global.setCurrentAuthInfo({ accountId, authToken, authStrategy });
    } catch (unknownErr) {
      /**
       * We currently ignore persistance failures, although the user
       * would have to log in again next time they use the app.
       */
      logger.warn({
        message: "[authenticateAccountUsingGoogleOAuthCode] Failed to persist auth token.",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });
    }
  };

  clearAuthToken = async () => {
    try {
      await localDb.global.setCurrentAuthInfo({
        accountId: null,
        authToken: null,
        authStrategy: null,
      });
    } catch (unknownErr) {
      logger.warn({
        message: "[authenticateAccountUsingGoogleOAuthCode] Failed to clear auth token.",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
    }
  };

  private static readonly CLEAR_BACKEND_EXTENSION_AUTH_COOKIE_TIMEOUT_MS = 3000;

  logOut = async () => {
    try {
      await pTimeout(this.clearBackendExtensionAuthCookie(), {
        milliseconds: AppStoreAuthStore.CLEAR_BACKEND_EXTENSION_AUTH_COOKIE_TIMEOUT_MS,
        fallback: () => {
          logger.error({
            message: "[logOut] Timed out clearing backend extension auth cookie.",
          });
        },
      }).catch(unknownErr => {
        logger.error({
          message: "[logOut] Failed to clear backend extension auth cookie.",
          info: {
            err: objectModule.safeErrorAsJson(unknownErr as BaseError),
          },
        });
      });
      await this.clearAuthToken();

      runInAction(() => {
        this.loggedInDuringThisSession = false;
        this.activeAuthStrategy = null;
        this.activeAuthToken = null;
        this.authenticateAccountUsingAppleOAuthStateState = buildAsyncData({});
        this.authenticateAccountUsingGoogleOAuthCodeState = buildAsyncData({});
      });

      this.assetAccessTokenState.reset();

      /**
       * Reset all event tracking data (Segment, GA, etc.)
       * This will ensure the next user that logs-in doesn't accidentally get
       * associated with the previous user.
       */
      const metricsPromise = metricsModule.resetTrackingData();

      /**
       * Clear all sync-related data.
       * Note that we do NOT clear the pending queued data (to prevent data-loss).
       * The next time the user logs in, this should get picked back up.
       */
      const dbPromises = [
        clientEnvModule.clearClientId(),
        clientEnvModule.clearClientGroupId(),
        electronModule.clearClientMetadata(),
        this.store.globalCache.clearAll(),
        localStorageModule.clearPendingEphemeralRouteAction(),
        // User-authored content, unlike the device-preference keys that
        // survive logout — the next account must not see this draft.
        localStorageModule.clearMemAgentComposerDraft(),
      ];

      // Maximum of 4 seconds to clear the data.
      const maxTimeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("[logOut] Clear operation timed out")), 4000)
      );

      const sideEffects: (unknown | Promise<unknown>)[] = [...dbPromises, metricsPromise];

      /**
       * If the side effects take too long, we should still
       * force a reload + logout to avoid leaving the device in a hanging state.
       */
      await Promise.race([Promise.all(sideEffects), maxTimeoutPromise]);
    } catch (unknownErr) {
      logger.error({
        message: "",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });
    }

    /**
     * Force-redirect after logging out.
     */
    const logInUrl = windowModule.buildUrl({
      path: appRoutes.logIn({}).path,
    });

    windowModule.navigateToInternal({
      url: logInUrl,
    });
  };

  private clearBackendExtensionAuthCookie = async (): Promise<void> => {
    await authApi.post("/v2/auth/logout", { credentials: "include" });
  };

  resetSystemStateForLogIn = async () => {
    clientEnvModule.clearClientId();
    await clientEnvModule.clearClientGroupId();
  };

  authenticateAccountUsingGoogleOAuthCode = async ({
    code,
    redirectURI,
    isSignUp,
    includeNativeChatOnboarding = true,
  }: {
    code: string;
    redirectURI: string;
    isSignUp: boolean;
    includeNativeChatOnboarding?: boolean;
  }) => {
    this.resetSystemStateForLogIn();
    this.updateAuthenticateAccountUsingGoogleOAuthCodeState(asyncDataLoading);

    try {
      const result = await (async () => {
        if (isSignUp) {
          return await authApi.post("/v2/auth/google/sign-up", {
            body: {
              google_oauth_code: code,
              redirect_uri: redirectURI,
              client_kind: "MEM_WEB_CLIENT",
              include_native_chat_onboarding: includeNativeChatOnboarding,
            },
          });
        }

        return await authApi.post("/v2/auth/google/log-in", {
          body: {
            google_oauth_code: code,
            redirect_uri: redirectURI,
            client_kind: "MEM_WEB_CLIENT",
            include_native_chat_onboarding: includeNativeChatOnboarding,
          },
        });
      })();

      if (!result.data) {
        throw new BaseError({
          message: "[_authActionAuthenticateAccountUsingGoogleOAuthCode] Failed.",
        });
      }

      const authenticatedAccountId = result.data.account_id;
      const token = result.data.oauth_access_token;
      const strategy = ApiAuthStrategy.Account;

      await this.storeAuthToken({
        accountId: authenticatedAccountId,
        authToken: token,
        authStrategy: strategy,
      });
      await this.setLastUsedAuthMethod(LastUsedAuthMethod.Google);

      this.updateAuthenticateAccountUsingGoogleOAuthCodeState(asyncData =>
        asyncDataLoaded(asyncData, true)
      );

      runInAction(() => {
        this.activeAuthToken = token;
        this.activeAuthStrategy = strategy;
        this.loggedInDuringThisSession = true;
      });
      if (isSignUp) {
        localStorageModule.writeIntentOnboardingSignupReceipt({
          accountId: authenticatedAccountId,
        });
      }
    } catch (unknownErr) {
      toastModule.triggerToast({ content: "Failed to login." });

      logger.error({
        message: "Failed to login. Google oauth",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });

      this.updateAuthenticateAccountUsingGoogleOAuthCodeState(asyncDataFailed);
    }
  };

  /**
   * Completes Apple sign-in after the callback redirects back with a state value.
   * The frontend sends that state to the backend, which validates the paired
   * nonce cookie and returns the Mem auth token for the authenticated account.
   */
  authenticateAccountUsingAppleOAuthState = async ({ state }: { state: string }) => {
    this.resetSystemStateForLogIn();
    this.updateAuthenticateAccountUsingAppleOAuthStateState(asyncDataLoading);

    try {
      const result = await authApi.rawPost("/v2/auth/apple/finalize", {
        body: { state },
      });

      const responseData = (result.data ?? {}) as {
        account_id?: Uuid;
        oauth_access_token?: string;
      };

      const accountId = responseData.account_id;
      const oauthAccessToken = responseData.oauth_access_token;

      if (!accountId || !oauthAccessToken) {
        throw new BaseError({
          message: "[authenticateAccountUsingAppleOAuthState] Failed.",
        });
      }

      const strategy = ApiAuthStrategy.Account;

      await this.storeAuthToken({
        accountId,
        authToken: oauthAccessToken,
        authStrategy: strategy,
      });
      await this.setLastUsedAuthMethod(LastUsedAuthMethod.Apple);

      this.updateAuthenticateAccountUsingAppleOAuthStateState(asyncData =>
        asyncDataLoaded(asyncData, true)
      );

      runInAction(() => {
        this.activeAuthToken = oauthAccessToken;
        this.activeAuthStrategy = strategy;
        this.loggedInDuringThisSession = true;
      });
    } catch (unknownErr) {
      toastModule.triggerToast({ content: "Failed to login." });

      logger.error({
        message: "Failed to login. Apple oauth",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });

      this.updateAuthenticateAccountUsingAppleOAuthStateState(asyncDataFailed);
    }
  };

  authenticateAccountUsingEmailPassword = async ({
    email,
    password,
    includeNativeChatOnboarding = true,
  }: {
    email: string;
    password: string;
    includeNativeChatOnboarding?: boolean;
  }): Promise<EmailPasswordAuthResult> => {
    this.resetSystemStateForLogIn();
    this.updateAuthenticateAccountUsingEmailPasswordState(asyncDataLoading);

    try {
      const result = await authApi.post("/v2/auth/email-password/log-in", {
        body: {
          email_address: email,
          password,
          include_native_chat_onboarding: includeNativeChatOnboarding,
        },
      });

      if (result.error) {
        this.updateAuthenticateAccountUsingEmailPasswordState(asyncDataFailed);
        return {
          success: false,
          errorMessage: extractEmailPasswordAuthErrorMessage({
            rawError: result.error,
            fallback: "Failed to log in",
          }),
        };
      }

      if (!result.data) {
        throw new BaseError({
          message: "[authenticateAccountUsingEmailPassword] Failed.",
        });
      }

      const authenticatedAccountId = result.data.account_id;
      const token = result.data.oauth_access_token;
      const strategy = ApiAuthStrategy.Account;

      await this.storeAuthToken({
        accountId: authenticatedAccountId,
        authToken: token,
        authStrategy: strategy,
      });
      await this.setLastUsedAuthMethod(LastUsedAuthMethod.EmailPassword);

      this.updateAuthenticateAccountUsingEmailPasswordState(asyncData =>
        asyncDataLoaded(asyncData, true)
      );

      runInAction(() => {
        this.activeAuthToken = token;
        this.activeAuthStrategy = strategy;
        this.loggedInDuringThisSession = true;
      });

      return { success: true };
    } catch (unknownErr) {
      toastModule.triggerToast({ content: "Failed to log in" });

      logger.error({
        message: "Failed to login. Email password",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });

      this.updateAuthenticateAccountUsingEmailPasswordState(asyncDataFailed);
      return {
        success: false,
        errorMessage: extractEmailPasswordAuthErrorMessage({
          rawError: unknownErr,
          fallback: "Failed to log in",
        }),
      };
    }
  };

  signUpUsingEmailPassword = async ({
    email,
    name,
    password,
  }: {
    email: string;
    name?: string;
    password: string;
  }): Promise<EmailPasswordAuthResult> => {
    try {
      const response = await authApi.post("/v2/auth/email-password/sign-up", {
        body: { email_address: email, password, profile_display_name: name },
      });

      if (response.error || !response.data?.success) {
        return {
          success: false,
          errorMessage: extractEmailPasswordAuthErrorMessage({
            rawError: response.error,
            fallback: "Failed to create account",
          }),
        };
      }

      localStorageModule.writeIntentOnboardingSignupReceipt({ email });

      return { success: true };
    } catch (unknownError) {
      return {
        success: false,
        errorMessage: extractEmailPasswordAuthErrorMessage({
          rawError: unknownError,
          fallback: "Failed to create account",
        }),
      };
    }
  };

  requestEmailPasswordVerification = async ({
    email,
  }: {
    email: string;
  }): Promise<EmailPasswordAuthResult> => {
    try {
      const response = await authApi.post("/v2/auth/email-password/request-email-verification", {
        body: { email_address: email },
      });

      if (response.error || !response.data?.success) {
        return {
          success: false,
          errorMessage: extractEmailPasswordAuthErrorMessage({
            rawError: response.error,
            fallback: "Failed to send verification email",
          }),
        };
      }

      return { success: true };
    } catch (unknownError) {
      return {
        success: false,
        errorMessage: extractEmailPasswordAuthErrorMessage({
          rawError: unknownError,
          fallback: "Failed to send verification email",
        }),
      };
    }
  };

  verifyEmailPasswordEmailAddress = async ({
    email,
    token,
  }: {
    email: string;
    token: string;
  }): Promise<EmailPasswordAuthResult> => {
    try {
      const response = await authApi.post("/v2/auth/email-password/verify-email", {
        body: { email_address: email, token },
      });

      if (response.error || !response.data?.success) {
        return {
          success: false,
          errorMessage: extractEmailPasswordAuthErrorMessage({
            rawError: response.error,
            fallback: "Failed to verify email",
          }),
        };
      }

      return { success: true };
    } catch (unknownError) {
      return {
        success: false,
        errorMessage: extractEmailPasswordAuthErrorMessage({
          rawError: unknownError,
          fallback: "Failed to verify email",
        }),
      };
    }
  };

  requestEmailPasswordReset = async ({
    email,
  }: {
    email: string;
  }): Promise<EmailPasswordAuthResult> => {
    try {
      const response = await authApi.post("/v2/auth/email-password/request-password-reset", {
        body: { email_address: email },
      });

      if (response.error || !response.data?.success) {
        return {
          success: false,
          errorMessage: extractEmailPasswordAuthErrorMessage({
            rawError: response.error,
            fallback: "Failed to send password reset email",
          }),
        };
      }

      return { success: true };
    } catch (unknownError) {
      return {
        success: false,
        errorMessage: extractEmailPasswordAuthErrorMessage({
          rawError: unknownError,
          fallback: "Failed to send password reset email",
        }),
      };
    }
  };

  resetEmailPassword = async ({
    email,
    token,
    password,
  }: {
    email: string;
    token: string;
    password: string;
  }): Promise<EmailPasswordAuthResult> => {
    try {
      const response = await authApi.post("/v2/auth/email-password/reset-password", {
        body: { email_address: email, token, password },
      });

      if (response.error || !response.data?.success) {
        return {
          success: false,
          errorMessage: extractEmailPasswordAuthErrorMessage({
            rawError: response.error,
            fallback: "Failed to reset password",
          }),
        };
      }

      return { success: true };
    } catch (unknownError) {
      return {
        success: false,
        errorMessage: extractEmailPasswordAuthErrorMessage({
          rawError: unknownError,
          fallback: "Failed to reset password",
        }),
      };
    }
  };

  authenticateAccountUsingOAuthToken = async ({
    accountId,
    oauthToken,
  }: {
    accountId: Uuid;
    oauthToken: string;
  }) => {
    this.resetSystemStateForLogIn();
    this.updateAuthenticateAccountUsingOAuthTokenState(asyncDataLoading);

    try {
      const strategy = ApiAuthStrategy.Account;

      localStorageModule.clearUpgradedUserEmail();

      await this.storeAuthToken({
        accountId: accountId,
        authToken: oauthToken,
        authStrategy: strategy,
      });

      this.updateAuthenticateAccountUsingOAuthTokenState(asyncData =>
        asyncDataLoaded(asyncData, true)
      );

      runInAction(() => {
        this.activeAuthToken = oauthToken;
        this.activeAuthStrategy = strategy;
        this.loggedInDuringThisSession = true;
      });
    } catch (unknownErr) {
      toastModule.triggerToast({ content: "Failed to login." });

      logger.error({
        message: "Failed to login. OAuth token",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });

      this.updateAuthenticateAccountUsingOAuthTokenState(asyncDataFailed);
    }
  };

  private startPeriodicCheck = () => {
    if (this.periodicCheckIntervalId === null) {
      this.periodicCheckIntervalId = globalThis.setInterval(
        this.periodicCheckIfAuthed,
        AppStoreAuthStore.PERIODIC_CHECK_INTERVAL
      );
    }
  };

  private stopPeriodicCheck = () => {
    if (this.periodicCheckIntervalId !== null) {
      globalThis.clearInterval(this.periodicCheckIntervalId);
      this.periodicCheckIntervalId = null;
    }
  };

  private handleExtensionAuthWindowClose = (authToken: string | null) => {
    if (!authToken) return;

    // Check if this auth session was initiated from Chrome extension
    const shouldCloseWindow = Cookies.get(EXTENSION_CLOSE_WINDOW_COOKIE_NAME) === "true";
    if (shouldCloseWindow) {
      // Clear the close window cookie
      Cookies.remove(EXTENSION_CLOSE_WINDOW_COOKIE_NAME, { path: "/" });
      // Close window after auth is complete
      window.close();
    }
  };
}

const getStringFromJson = (json: Record<string, unknown>, key: string) => {
  const s = json[key];
  if (isString(s)) return s;
};
